import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  History,
  Clock,
  Phone as PhoneIcon,
  CheckCircle2,
  XCircle,
  Hourglass,
  Trash2,
  AlertTriangle,
  Radio,
  ChevronDown,
} from "lucide-react";
import { formatTicketCode } from "../lib/ticketCode";

/**
 * Daily history of tickets per queue, displayed at the very bottom of the
 * merchant dashboard. Tabs are one per (queue, day) tuple.
 *
 * - `GET /merchant/history/sessions` : list of (queueId, queueName, date, count)
 * - `GET /merchant/history/tickets?queueId&date` : tickets for one tab
 * - `DELETE /merchant/history/tickets?queueId&date` : permanently wipe a day
 *
 * The component re-fetches sessions whenever the dashboard signals a change
 * (`refreshKey` prop bumped from the parent so newly-created tickets show up
 * without needing a page reload).
 */
export default function TicketsHistoryPanel({ refreshKey = 0 }) {
  const [sessions, setSessions] = useState([]);
  const [activeKey, setActiveKey] = useState(null); // `${queueId}|${date}`
  const [queueFilter, setQueueFilter] = useState("all"); // "all" or queueId — narrows the date tabs
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [data, setData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // {queueId, queueName, date}
  const [deleting, setDeleting] = useState(false);

  const loadSessions = async () => {
    setLoadingSessions(true);
    try {
      const { data: rows } = await api.get("/merchant/history/sessions");
      setSessions(rows || []);
      if (rows && rows.length) {
        const first = `${rows[0].queueId}|${rows[0].date}`;
        setActiveKey((prev) => prev && rows.find((r) => `${r.queueId}|${r.date}` === prev) ? prev : first);
      } else {
        setActiveKey(null);
        setData(null);
      }
    } catch {
      // Non-blocking — the panel is purely informational
    }
    setLoadingSessions(false);
  };

  useEffect(() => { loadSessions(); /* eslint-disable-next-line */ }, [refreshKey]);

  useEffect(() => {
    if (!activeKey) return;
    const [queueId, date] = activeKey.split("|");
    setLoadingData(true);
    api
      .get(`/merchant/history/tickets`, { params: { queueId, date } })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoadingData(false));
  }, [activeKey, refreshKey]);

  const grouped = useMemo(() => {
    // Sessions are already sorted by date desc. Group them by date for readability.
    const map = new Map();
    for (const s of sessions) {
      if (queueFilter !== "all" && s.queueId !== queueFilter) continue;
      if (!map.has(s.date)) map.set(s.date, []);
      map.get(s.date).push(s);
    }
    return Array.from(map.entries());
  }, [sessions, queueFilter]);

  // Distinct queues present in the history — used to render the queue filter.
  const queuesInHistory = useMemo(() => {
    const seen = new Map();
    for (const s of sessions) {
      if (!seen.has(s.queueId)) seen.set(s.queueId, s.queueName);
    }
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [sessions]);

  // When the queue filter narrows the visible tabs, keep the active tab
  // consistent (pick the first matching session if the old one no longer
  // matches the new filter).
  useEffect(() => {
    if (!activeKey) return;
    const [qid] = activeKey.split("|");
    if (queueFilter !== "all" && qid !== queueFilter) {
      const firstMatch = sessions.find((s) => s.queueId === queueFilter);
      if (firstMatch) setActiveKey(`${firstMatch.queueId}|${firstMatch.date}`);
    }
  }, [queueFilter, sessions, activeKey]);

  const askDelete = () => {
    if (!data) return;
    setConfirmDel({ queueId: data.queueId, queueName: data.queueName, date: data.date });
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await api.delete(`/merchant/history/tickets`, {
        params: { queueId: confirmDel.queueId, date: confirmDel.date },
      });
      toast.success("Historique supprimé");
      setConfirmDel(null);
      // Refresh the sessions list — the deleted tab disappears
      await loadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    }
    setDeleting(false);
  };

  return (
    <section className="mt-6 sm:mt-10 rounded-2xl border border-border bg-white shadow-sm overflow-hidden" data-testid="tickets-history-panel">
      <header className="px-4 sm:px-5 py-3 border-b border-border bg-surface/60 flex items-center gap-3">
        <span aria-hidden="true" className="w-9 h-9 rounded-full bg-brand/10 text-brand inline-flex items-center justify-center">
          <History size={18} />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-black uppercase tracking-[0.08em] text-base sm:text-lg text-ink leading-tight">
            Historique des tickets
          </h3>
          <p className="text-[11px] sm:text-xs text-subtle">Consultez l'activité de chaque file par journée.</p>
        </div>
      </header>

      {loadingSessions ? (
        <div className="p-6 text-center text-subtle text-sm" data-testid="history-loading">Chargement…</div>
      ) : sessions.length === 0 ? (
        <div className="p-8 text-center" data-testid="history-empty">
          <p className="text-sm font-bold text-ink">Aucun historique pour le moment</p>
          <p className="text-xs text-subtle mt-1">L'historique apparaîtra dès qu'un visiteur prendra un ticket.</p>
        </div>
      ) : (
        <>
          {/* Queue filter — only when 2+ distinct queues appear in history */}
          {queuesInHistory.length >= 2 && (
            <div className="px-3 sm:px-4 pt-3 pb-2 border-b border-border flex items-center gap-3 flex-wrap" data-testid="history-queue-filter-row">
              <label className="text-[11px] font-bold uppercase tracking-wider text-subtle">
                File
              </label>
              <div className="relative inline-block">
                <select
                  value={queueFilter}
                  onChange={(e) => setQueueFilter(e.target.value)}
                  className="appearance-none pl-3 pr-9 h-9 rounded-md border border-border bg-white text-ink font-display font-bold text-xs uppercase tracking-wider shadow-sm hover:border-ink focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand cursor-pointer transition-colors"
                  data-testid="history-queue-filter"
                  aria-label="Filtrer l'historique par file"
                >
                  <option value="all">Toutes les files</option>
                  {queuesInHistory.map((q) => (
                    <option key={q.id} value={q.id}>{q.name}</option>
                  ))}
                </select>
                <span
                  aria-hidden="true"
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-5 h-5 rounded-md bg-ink/5 text-ink pointer-events-none"
                >
                  <ChevronDown size={12} strokeWidth={3} />
                </span>
              </div>
              <span className="text-[11px] text-subtle">
                {queueFilter === "all"
                  ? `${queuesInHistory.length} files`
                  : "1 file sélectionnée"}
              </span>
            </div>
          )}

          {/* Tabs (scroll horizontal on mobile) */}
          <div className="px-3 sm:px-4 pt-3 pb-2 border-b border-border overflow-x-auto" data-testid="history-tabs">
            <div className="flex flex-wrap gap-2 min-w-max sm:min-w-0">
              {grouped.map(([date, rows]) => (
                <div key={date} className="flex flex-wrap items-center gap-2">
                  {rows.map((s) => {
                    const k = `${s.queueId}|${s.date}`;
                    const active = k === activeKey;
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setActiveKey(k)}
                        aria-pressed={active}
                        className={`inline-flex items-center gap-2 h-9 px-3 rounded-md border text-xs font-bold uppercase tracking-wider transition-colors whitespace-nowrap ${
                          active
                            ? "bg-ink text-white border-ink shadow-sm"
                            : "bg-white text-ink border-border hover:border-ink"
                        }`}
                        data-testid={`history-tab-${s.queueId}-${s.date}`}
                      >
                        <span className="truncate max-w-[140px]">{s.queueName}</span>
                        <span className="text-[10px] opacity-80 font-mono">{formatDateShort(s.date)}</span>
                        <span className={`ml-1 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded text-[10px] font-mono ${active ? "bg-white text-ink" : "bg-ink text-white"}`}>
                          {s.count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* Active session content */}
          <div className="p-4 sm:p-5">
            {loadingData ? (
              <p className="text-sm text-subtle text-center py-6">Chargement…</p>
            ) : !data ? null : (
              <>
                <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-xs sm:text-sm text-ink space-y-0.5">
                    <p>
                      <strong className="font-bold">{data.queueName}</strong>
                      <span className="text-subtle"> · {formatDateLong(data.date)}</span>
                    </p>
                    <p className="text-[11px] sm:text-xs text-subtle flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span><strong className="text-ink font-bold">{data.count}</strong> ticket{data.count > 1 ? "s" : ""}</span>
                      <span>
                        Attente moyenne&nbsp;: <strong className="text-ink font-bold">{data.avgWaitMinutes != null ? `${data.avgWaitMinutes} min` : "—"}</strong>
                      </span>
                    </p>
                  </div>
                  <button
                    onClick={askDelete}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-destructive/40 text-destructive hover:bg-destructive hover:text-white text-xs font-bold uppercase tracking-wider transition-colors"
                    data-testid="history-delete-btn"
                  >
                    <Trash2 size={12} /> Supprimer l'historique
                  </button>
                </div>

                {data.tickets.length === 0 ? (
                  <p className="text-sm text-subtle text-center py-6" data-testid="history-tickets-empty">Aucun ticket pour cette journée.</p>
                ) : (
                  <ul className="divide-y divide-border border border-border rounded-lg overflow-hidden" data-testid="history-tickets-list">
                    {data.tickets.map((t) => (
                      <HistoryRow key={t.id} t={t} />
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Confirm-delete modal */}
      {confirmDel && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => !deleting && setConfirmDel(null)}
          data-testid="history-delete-confirm-backdrop"
        >
          <div
            className="bg-white border-2 border-destructive max-w-md w-full rounded-xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            data-testid="history-delete-confirm-modal"
          >
            <div className="px-5 py-4 border-b border-border bg-destructive/5 flex items-center gap-3">
              <AlertTriangle size={22} className="text-destructive shrink-0" />
              <h4 className="font-display text-lg font-black text-ink">
                Supprimer cet historique&nbsp;?
              </h4>
            </div>
            <div className="px-5 py-5 text-sm text-ink leading-relaxed space-y-3">
              <p>
                Êtes-vous sûr de vouloir supprimer l'historique de la file
                <strong> « {confirmDel.queueName} »</strong> du <strong>{formatDateLong(confirmDel.date)}</strong>&nbsp;?
              </p>
              <p className="text-destructive font-bold">Cette action est irréversible.</p>
            </div>
            <div className="px-5 py-4 bg-surface/50 border-t border-border flex flex-wrap justify-end gap-2">
              <button
                disabled={deleting}
                onClick={() => setConfirmDel(null)}
                className="inline-flex items-center justify-center h-9 px-4 rounded-md border border-ink text-ink text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                data-testid="history-delete-cancel-btn"
              >
                Annuler
              </button>
              <button
                disabled={deleting}
                onClick={doDelete}
                className="inline-flex items-center justify-center h-9 px-5 rounded-md bg-destructive text-white text-xs font-bold uppercase tracking-wider hover:bg-destructive/90 disabled:opacity-50"
                data-testid="history-delete-confirm-btn"
              >
                {deleting ? "…" : "OK"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function HistoryRow({ t }) {
  const meta = STATUS_META[t.status] || STATUS_META.waiting;
  const Icon = meta.Icon;
  const taken = formatTime(t.createdAt);
  const called = formatTime(t.calledAt);
  return (
    <li className="flex flex-wrap items-center gap-3 px-3 sm:px-4 py-2.5 text-sm" data-testid={`history-row-${t.id}`}>
      <span className="font-mono text-base sm:text-lg font-bold text-ink min-w-[3.5rem]">{formatTicketCode(t) ?? "—"}</span>
      <span className="inline-flex items-center gap-1 text-xs text-subtle">
        <Clock size={12} /> Pris à {taken}
      </span>
      {called && (
        <span className="inline-flex items-center gap-1 text-xs text-subtle">
          <PhoneIcon size={12} /> Appelé à {called}
        </span>
      )}
      {typeof t.gapMin === "number" && (
        <span className="inline-flex items-center gap-1 text-[11px] text-subtle bg-surface border border-border rounded px-1.5 py-0.5" title="Écart avec le ticket précédent">
          +{t.gapMin} min
        </span>
      )}
      {t.type === "priority" && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-brand text-white text-[10px] font-bold uppercase">
          <Radio size={9} /> Distance
        </span>
      )}
      <span className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider ${meta.cls}`}>
        <Icon size={11} /> {meta.label}
      </span>
    </li>
  );
}

const STATUS_META = {
  waiting:  { label: "En attente", Icon: Hourglass,    cls: "bg-surface text-subtle border border-border" },
  called:   { label: "Appelé",     Icon: PhoneIcon,    cls: "bg-brand/10 text-brand" },
  present:  { label: "Validé",     Icon: CheckCircle2, cls: "bg-[#0A7F2E]/10 text-[#0A7F2E]" },
  absent:   { label: "Absent",     Icon: XCircle,      cls: "bg-destructive/10 text-destructive" },
  cancelled:{ label: "Annulé",     Icon: XCircle,      cls: "bg-subtle/10 text-subtle border border-border" },
  expired:  { label: "Expiré",     Icon: Hourglass,    cls: "bg-[#F18F18]/10 text-[#F18F18]" },
};

function formatTime(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" });
  } catch {
    return null;
  }
}

function formatDateShort(yyyymmdd) {
  // 2026-05-08 → 08/05
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(yyyymmdd || "");
  if (!m) return yyyymmdd || "";
  return `${m[3]}/${m[2]}`;
}

function formatDateLong(yyyymmdd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(yyyymmdd || "");
  if (!m) return yyyymmdd || "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}
